import { useEffect, useRef, useState } from "react";
import { useLiveActivity } from "../hooks/useLiveActivity";
import {
  getConcernCounts,
  getLeadsByCategory,
  getLeadsByLossReason,
  getLeadsBySource,
  getLeadsByType,
  getLeadStatusCounts,
  getLocations,
  getNeedsAttention,
  getServiceInterestCounts,
} from "../services/api";
import { formatLossReason, LEAD_CATEGORIES, LEAD_SOURCES, LEAD_STATUSES, LEAD_TYPES } from "../utils/displayFormatters";
import BarListChart from "./charts/BarListChart";
import LeadsByDate from "./charts/LeadsByDate";
import NeedsAttention from "./charts/NeedsAttention";
import StatusFunnel from "./charts/StatusFunnel";
import StatusStrip from "./charts/StatusStrip";
import NotificationBanner from "./NotificationBanner";
import ProspectsToggle from "./ProspectsToggle";
import UnreadToggle from "./UnreadToggle";
import { useUser } from "./UserContext";

const Homepage = ({ authToken }) => {
  const { activityVersion } = useLiveActivity();
  const seenActivityVersion = useRef(activityVersion);
  const DATE_RANGE_OPTIONS = [
    { label: "Today", value: "1" },
    { label: "Last 7 days", value: "7" },
    { label: "Last 30 days", value: "30" },
    { label: "Last 60 days", value: "60" },
    { label: "Last 90 days", value: "90" },
    { label: "All time", value: "all" },
  ];

  const [leadCategoryData, setLeadCategoryData] = useState([]);
  const [leadSourceData, setLeadSourceData] = useState([]);
  const [leadTypeData, setLeadTypeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadStatusData, setLeadStatusData] = useState([]);
  const [leadLossReasonData, setLeadLossReasonData] = useState([]);
  const [concernCounts, setConcernCounts] = useState([]);
  const [serviceInterestCounts, setServiceInterestCounts] = useState([]);
  const [needsAttention, setNeedsAttention] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    return localStorage.getItem("global_date_range") || "30";
  });
  const [prospectsOnly, setProspectsOnly] = useState(
    () => localStorage.getItem("global_prospects_only") !== "false",
  );
  const [unreadOnly, setUnreadOnly] = useState(
    () => localStorage.getItem("global_unread_only") === "true",
  );
  const { locationInfo, setLocationInfo } = useUser();

  const dateRangeParam = dateRange;
  // Global "Prospects only" filter → lead_category=prospect when ON.
  const leadCategoryParam = prospectsOnly ? "prospect" : "";
  const dateRangeLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === dateRange)?.label ||
    "";

  const buildLeadsLink = (params = {}) => {
    const searchParams = new URLSearchParams(params);
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        searchParams.delete(key);
        value.forEach((entry) => searchParams.append(key, entry));
      }
    });
    if (dateRangeParam) {
      searchParams.set("days", dateRangeParam);
    }
    const query = searchParams.toString();
    return query ? `/leads?${query}` : "/leads";
  };

  const transformLeadStatusData = (data) => {
    return data.map((item) => {
      const statusObj = LEAD_STATUSES.find(
        (s) => s.key === item.name?.toLowerCase()
      );
      return {
        ...item,
        key: statusObj?.key || item.name,
        label: statusObj?.label || item.name,
      };
    });
  };

  useEffect(() => {
    // A slower earlier request must not overwrite a newer one when filters
    // change quickly.
    let cancelled = false;
    // New activity refreshes the counts without dimming the page.
    const silent = seenActivityVersion.current !== activityVersion;
    seenActivityVersion.current = activityVersion;
    const fetchDashboardData = async () => {
      try {
        if (!silent) setLoading(true);
        setError(null);

        // Fetch all count data in parallel
        const [
          statusCounts,
          typeCounts,
          categoryCounts,
          sourceCounts,
          lossReasonResponse,
          concerns,
          serviceInterest,
          needsAttentionData,
        ] = await Promise.all([
          getLeadStatusCounts(dateRangeParam, '', '', leadCategoryParam, unreadOnly),
          getLeadsByType(dateRangeParam, '', '', leadCategoryParam, unreadOnly),
          getLeadsByCategory(dateRangeParam, '', '', unreadOnly),
          getLeadsBySource(dateRangeParam, '', '', leadCategoryParam, unreadOnly),
          getLeadsByLossReason(dateRangeParam, '', '', leadCategoryParam, unreadOnly),
          getConcernCounts(dateRangeParam, '', '', leadCategoryParam, unreadOnly),
          getServiceInterestCounts(dateRangeParam, '', '', leadCategoryParam, unreadOnly),
          getNeedsAttention(leadCategoryParam, unreadOnly),
        ]);

        if (cancelled) return;
        setLeadStatusData(transformLeadStatusData(statusCounts));
        setLeadTypeData(typeCounts);
        setLeadCategoryData(categoryCounts);
        setLeadSourceData(sourceCounts);
        setLeadLossReasonData(lossReasonResponse);
        setConcernCounts(concerns);
        setServiceInterestCounts(serviceInterest);
        setNeedsAttention(needsAttentionData);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        console.error("Dashboard data fetch error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDashboardData();
    return () => {
      cancelled = true;
    };
  }, [dateRangeParam, leadCategoryParam, unreadOnly, activityVersion]);

  useEffect(() => {
    localStorage.setItem("global_date_range", dateRange);
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem("global_prospects_only", String(prospectsOnly));
  }, [prospectsOnly]);

  useEffect(() => {
    localStorage.setItem("global_unread_only", String(unreadOnly));
  }, [unreadOnly]);

  // Locations tell the worklist which sites can receive texts (for analytics).
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLocations(await getLocations());
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, []);

  // Only the first load replaces the page. After that the page stays up and
  // dims while it refreshes, so a filter click never blanks the screen.
  if (loading && needsAttention === null)
    return <div className="text-center py-8">Loading dashboard...</div>;
  if (error)
    return <div className="text-center py-8 text-red-600">{error}</div>;

  const sectionHeading = (text) => (
    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
      {text}
    </h2>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto px-4 pt-24 pb-8 md:py-8 shadow-sm rounded-lg">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center space-x-2 mb-8">
          {locationInfo?.company?.name && locationInfo.company.name + " "}
          Dashboard
        </h1>

        <NotificationBanner />

        {/* Every filter in one bar, laid out like the Leads page. Prospects and
            Unread apply to the whole page; the date range applies to Insights. */}
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-x-6 mb-2">
          <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap md:items-center">
            {DATE_RANGE_OPTIONS.map((option) => {
              const isActive = option.value === dateRange;
              return (
                <button
                  key={option.value}
                  data-attr="dashboard-date-range"
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setDateRange(option.value)}
                  className={`px-1 md:px-3 py-1.5 rounded-full text-[13px] md:text-sm font-medium whitespace-nowrap transition-colors border ${
                    isActive
                      ? "bg-teal-600 text-white border-teal-600"
                      : "bg-white text-gray-700 border-border hover:border-teal-300 hover:text-teal-700"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center [&>button]:justify-between md:[&>button]:justify-start">
            <ProspectsToggle
              value={prospectsOnly}
              onChange={setProspectsOnly}
            />
            <UnreadToggle value={unreadOnly} onChange={setUnreadOnly} />
          </div>
        </div>

        {/* Refresh indicator. The slot is always there so the page does not jump. */}
        <div className="h-1 mb-3" role="status">
          {loading && (
            <>
              <div className="h-1 rounded-full bg-teal-600 animate-pulse" />
              <span className="sr-only">Updating dashboard…</span>
            </>
          )}
        </div>

        <div
          aria-busy={loading}
          className={`transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}
        >
          {/* Below md the page is one column and the sections reorder with `order-N`:
            status strip, Needs attention, funnel, then the rest. `contents` lets the
            nested sections join that column. From md up nothing changes. */}
          <div className="flex flex-col gap-6 md:block">
            {/* 1. What needs doing right now. Ignores the date range. */}
            <div className="order-2 md:order-none md:mb-10">
              <NeedsAttention
                data={needsAttention}
                locationIds={locations
                  .filter((location) => location.messaging_enabled)
                  .map((location) => location.id)}
              />
            </div>

            {/* 2. Insights. Follows the date range in the filter bar above. */}
            <div className="contents md:block">
              <div className="order-1 md:order-none md:mb-4">
                {sectionHeading(`Insights · ${dateRangeLabel}`)}
              </div>
              <div className="order-1 md:order-none">
                <StatusStrip
                  leadStatusData={leadStatusData}
                  days={dateRangeParam}
                  dateRangeLabel={dateRangeLabel}
                />
              </div>
              <div className="order-4 md:order-none grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-8 md:mt-8">
                <LeadsByDate
                  dateRangeParam={dateRangeParam}
                  dateRangeLabel={dateRangeLabel}
                  leadCategory={leadCategoryParam}
                  unreadOnly={unreadOnly}
                />
              </div>

              <div className="contents md:grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                <div className="order-3 md:order-none">
                  <StatusFunnel
                    leadStatusData={leadStatusData}
                    buildLeadsLink={buildLeadsLink}
                  />
                </div>
                <div className="order-5 md:order-none space-y-8">
                  <BarListChart
                    title="What leads were worried about"
                    subtitle="The concern each lead raised, as our AI read it."
                    data={concernCounts}
                    emptyMessage="No concerns recorded yet"
                    emptyHint="Our AI records a concern when a lead raises one in a call, message or form."
                  />
                  <BarListChart
                    title="Treatments they asked about"
                    subtitle="Which of your services leads mentioned."
                    data={serviceInterestCounts}
                    emptyMessage="No treatment interest recorded yet"
                    emptyHint="Our AI matches what a lead asks about against your service list."
                    formatSecondary={(row) =>
                      row.estimated_value
                        ? `$${Math.round(row.estimated_value).toLocaleString()}`
                        : null
                    }
                    caveat="Values come from your service price list. Use them to compare treatments against each other, not as revenue earned."
                  />
                </div>
              </div>

              <div className="order-6 md:order-none grid grid-cols-1 lg:grid-cols-2 gap-8 md:mt-8">
                <BarListChart
                  title="Leads by type"
                  subtitle="How each lead reached you: phone, chatbot, form and so on."
                  // A lead with no type has no Leads-page filter to open, so it is left out.
                  data={leadTypeData
                    .filter((item) => item.name)
                    .map((item) => ({
                      ...item,
                      label:
                        LEAD_TYPES.find((t) => t.key === item.name)?.label ||
                        item.name,
                    }))
                    .sort((a, b) => b.value - a.value)}
                  emptyMessage="No leads in this range"
                  linkFor={(row) => buildLeadsLink({ type: row.name })}
                />
                <BarListChart
                  title="Leads by category"
                  subtitle="Prospects, existing patients and the rest."
                  data={leadCategoryData.map((item) => ({
                    ...item,
                    label:
                      LEAD_CATEGORIES.find((c) => c.key === item.name)?.label ||
                      item.name,
                  }))}
                  emptyMessage="No leads in this range"
                  linkFor={(row) => buildLeadsLink({ lead_category: row.name })}
                />
                <BarListChart
                  title="Leads by source"
                  subtitle="Where each lead came from."
                  data={leadSourceData.map((item) => ({
                    ...item,
                    label:
                      LEAD_SOURCES.find((s) => s.key === item.name)?.label ||
                      item.name,
                  }))}
                  emptyMessage="No leads in this range"
                  linkFor={(row) => buildLeadsLink({ source: row.name })}
                />
                <BarListChart
                  title="Why leads were lost"
                  subtitle={`${leadLossReasonData?.total_lost_leads || 0} lost leads in this range.`}
                  data={(leadLossReasonData?.chart_data || []).map((item) => ({
                    ...item,
                    label:
                      item.label === "No Reason Specified"
                        ? item.label
                        : item.label || formatLossReason(item.name),
                  }))}
                  emptyMessage="No lost leads in this range"
                  linkFor={(row) => buildLeadsLink({ loss_reason: row.name })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
